import type { CellObject, ColInfo, WorkSheet } from "xlsx";
import { formatDurationHms } from "@/lib/format/duration";

/**
 * One spreadsheet cell. Durations carry seconds rather than a formatted string so the writer can
 * emit a real Excel duration; `blank` is an intentionally empty cell, not a missing value.
 */
export type ExcelCellInput =
  | { kind: "text"; value: string }
  | { kind: "number"; value: number }
  | { kind: "duration"; seconds: number }
  | { kind: "blank" };

/** The Excel-facing half of a table column, shared by the History and Reports exporters. */
export interface ExportCellSpec {
  /** Appended to the header by `withUnit` when the label does not already carry the unit. */
  unit?: string;
  /** How the cell is written. Defaults to text. */
  excel?: "number" | "duration";
  /** Replaces the header label entirely (a duration header states hh:mm:ss, not min). */
  excelLabel?: string;
}

export interface ExcelSheetSpec {
  sheetName: string;
  /** Without the `.xlsx` extension. */
  fileName: string;
  headers: string[];
  rows: ExcelCellInput[][];
}

/** Excel duration format. Bracketed hours so a 30-hour total reads `30:15:00`, not `06:15:00`. */
const DURATION_NUMBER_FORMAT = "[h]:mm:ss";
const SECONDS_PER_DAY = 86400;

const MIN_COLUMN_WCH = 8;
const MAX_COLUMN_WCH = 44;
/** Excel's own cell inset, in characters. */
const CELL_PADDING_WCH = 2;
/** The AutoFilter dropdown button overlaps the header text. */
const FILTER_BUTTON_WCH = 3;

/** Surrogate-safe, so diacritics and Cyrillic count as one character. */
function charLength(value: string): number {
  return Array.from(value).length;
}

/**
 * Appends `(unit)` unless the label already ends with that exact parenthetical — report labels are
 * inconsistent, with `Total Distance (km)` and a bare `Avg Speed` sitting in the same table.
 */
export function withUnit(label: string, unit?: string): string {
  const trimmed = unit?.trim();
  if (!trimmed) return label;
  const existing = label.match(/\(([^()]*)\)\s*$/)?.[1]?.trim();
  if (existing && existing.toLocaleLowerCase() === trimmed.toLocaleLowerCase()) return label;
  return `${label} (${trimmed})`;
}

/**
 * Maps one already-resolved value to a cell. Callers pass the *unformatted* value — the sheet can
 * then be sorted, filtered and summed, unlike the display strings the table and PDF share.
 */
export function toExcelCell(value: unknown, spec: ExportCellSpec): ExcelCellInput {
  if (value === null || value === undefined || value === "") return { kind: "blank" };

  if (spec.excel === "duration" && typeof value === "number") {
    return { kind: "duration", seconds: value };
  }
  if (spec.excel === "number" && typeof value === "number" && Number.isFinite(value)) {
    return { kind: "number", value };
  }
  // Untyped columns still export as numbers when the caller already holds one.
  if (typeof value === "number") {
    return Number.isFinite(value) ? { kind: "number", value } : { kind: "blank" };
  }
  return { kind: "text", value: String(value) };
}

/** `book_append_sheet` throws on `[]:*?/\` or names over 31 characters. */
export function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(/[[\]:*?/\\]/g, " ").replace(/\s+/g, " ").trim().slice(0, 31);
  return cleaned || "Report";
}

/** What the cell will look like on screen — a duration measures as `12:34:56`, not as its serial. */
function renderedText(cell: ExcelCellInput): string {
  switch (cell.kind) {
    case "text":
      return cell.value;
    case "number":
      return String(cell.value);
    case "duration":
      return formatDurationHms(cell.seconds);
    case "blank":
      return "";
  }
}

function toSheetValue(cell: ExcelCellInput): string | number | CellObject | undefined {
  switch (cell.kind) {
    case "text":
      return cell.value;
    case "number":
      return cell.value;
    case "duration":
      // A cell object bypasses SheetJS type inference, so `t` has to be set explicitly.
      return { t: "n", v: cell.seconds / SECONDS_PER_DAY, z: DURATION_NUMBER_FORMAT };
    case "blank":
      return undefined;
  }
}

function autoFitColumns(headers: string[], rows: ExcelCellInput[][]): ColInfo[] {
  const widths = headers.map((header) => charLength(header) + FILTER_BUTTON_WCH);
  for (const row of rows) {
    for (let col = 0; col < widths.length; col += 1) {
      const cell = row[col];
      if (!cell) continue;
      const width = charLength(renderedText(cell));
      if (width > widths[col]) widths[col] = width;
    }
  }
  return widths.map((width) => ({
    wch: Math.min(MAX_COLUMN_WCH, Math.max(MIN_COLUMN_WCH, width + CELL_PADDING_WCH)),
  }));
}

/**
 * Builds and downloads a single-sheet workbook with auto-fitted columns and an AutoFilter header.
 *
 * `xlsx` is imported here rather than at module scope: it is ~230 KB gzip and must stay in its own
 * async chunk instead of landing in the entry chunk of every page that can export.
 */
export async function writeXlsxFile(spec: ExcelSheetSpec): Promise<void> {
  const XLSX = await import("xlsx");
  XLSX.writeFile(buildWorkbook(XLSX, spec), `${spec.fileName}.xlsx`);
}

/**
 * The same workbook as `writeXlsxFile`, as bytes. Used by the scheduled-report renderer, which
 * runs on the server and attaches the file to an email instead of downloading it.
 */
export async function renderXlsx(spec: ExcelSheetSpec): Promise<Uint8Array> {
  const XLSX = await import("xlsx");
  const out = XLSX.write(buildWorkbook(XLSX, spec), { type: "array", bookType: "xlsx" }) as ArrayBuffer;
  return new Uint8Array(out);
}

function buildWorkbook(XLSX: typeof import("xlsx"), spec: ExcelSheetSpec) {
  const { headers, rows } = spec;

  const body = rows.map((row) => headers.map((_, col) => toSheetValue(row[col] ?? { kind: "blank" })));
  const ws: WorkSheet = XLSX.utils.aoa_to_sheet([headers, ...body]);

  ws["!cols"] = autoFitColumns(headers, rows);

  const ref = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: rows.length, c: Math.max(0, headers.length - 1) },
  });
  // `aoa_to_sheet` skips blank cells without growing the range, so a trailing blank would leave
  // `!ref` short of the filter range and Excel would report the file as needing repair.
  ws["!ref"] = ref;
  ws["!autofilter"] = { ref };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(spec.sheetName));
  return wb;
}
